/**
 * Section RÉUTILISABLE « Forfaits transparents + Confiance » pour les pages
 * chauffeur — le combo que l'analyse concurrentielle désigne comme gagnant et
 * qu'AUCUN concurrent local n'a (prix affichés + avis + E-E-A-T réunis).
 *
 * 100 % données RÉELLES (zéro invention) :
 * - forfaits : VEHICLES.CLASS_E.fixedTransfers (classe la plus accessible) + €/km
 * - note : AGGREGATE_RATING (Google 5,0/5 · 38 avis)
 * - entité : COMPANY.legal (SIREN, fondé 2022)
 *
 * Composant serveur (aucune interactivité) → bon pour le SSR/SEO. i18n FR/EN/AR,
 * classes logiques (RTL-safe). La licence VTC n'est JAMAIS affichée (info privée).
 */
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { VEHICLES } from "@/config/infos";
import { AGGREGATE_RATING } from "@/config/reputation";
import { COMPANY } from "@/config/company";

/** Trajets vitrine (clés VEHICLES.fixedTransfers) + libellés localisés. */
const HEADLINE_ROUTES: { key: string; label: I18n<string> }[] = [
  {
    key: "CANNES__NCE_AIRPORT",
    label: {
      fr: "Cannes ⇄ Aéroport de Nice",
      en: "Cannes ⇄ Nice Airport",
      ar: "كان ⇄ مطار نيس",
    },
  },
  {
    key: "CANNES__MONACO",
    label: { fr: "Cannes ⇄ Monaco", en: "Cannes ⇄ Monaco", ar: "كان ⇄ موناكو" },
  },
  {
    key: "MONACO__NCE_AIRPORT",
    label: {
      fr: "Monaco ⇄ Aéroport de Nice",
      en: "Monaco ⇄ Nice Airport",
      ar: "موناكو ⇄ مطار نيس",
    },
  },
  {
    key: "CANNES__SAINT_TROPEZ",
    label: {
      fr: "Cannes ⇄ Saint-Tropez",
      en: "Cannes ⇄ Saint-Tropez",
      ar: "كان ⇄ سان تروبيه",
    },
  },
];

type Dict = {
  eyebrow: string;
  heading: string;
  intro: string;
  forfaitsHeading: string;
  fromWord: string;
  perKmNote: (perKm: string) => string;
  trustHeading: string;
  ratingItem: (value: string, count: number) => string;
  sirenItem: (siren: string) => string;
  since2022: string;
  available247: string;
  insured: string;
  fixedNoSurprise: string;
  cta: string;
};

const UI: I18n<Dict> = {
  fr: {
    eyebrow: "Tarifs & confiance",
    heading: "Forfaits transparents, exécution premium",
    intro:
      "Contrairement à la plupart des agences, nous affichons nos prix. Forfaits fixes définis à l'avance — pas de surprise à l'arrivée.",
    forfaitsHeading: "Forfaits chauffeur (à partir de)",
    fromWord: "dès",
    perKmNote: (perKm) =>
      `Au-delà des forfaits : dès ${perKm} €/km. Devis immédiat pour tout autre trajet.`,
    trustHeading: "Pourquoi nous faire confiance",
    ratingItem: (value, count) => `Note ${value}/5 — ${count} avis Google`,
    sirenItem: (siren) => `Entreprise déclarée — SIREN ${siren}`,
    since2022: "Active depuis 2022, basée sur la Côte d'Azur",
    available247: "Disponible 24h/24, 7j/7 — suivi de vol inclus",
    insured: "Chauffeurs professionnels, assurance pro, licence VTC",
    fixedNoSurprise: "Forfaits fixes payés au CA réel, sans frais cachés",
    cta: "Réserver ou demander un devis",
  },
  en: {
    eyebrow: "Pricing & trust",
    heading: "Transparent flat rates, premium execution",
    intro:
      "Unlike most agencies, we show our prices. Flat rates set in advance — no surprise on arrival.",
    forfaitsHeading: "Chauffeur flat rates (from)",
    fromWord: "from",
    perKmNote: (perKm) =>
      `Beyond flat rates: from €${perKm}/km. Instant quote for any other route.`,
    trustHeading: "Why trust us",
    ratingItem: (value, count) => `Rated ${value}/5 — ${count} Google reviews`,
    sirenItem: (siren) => `Registered company — SIREN ${siren}`,
    since2022: "Operating since 2022, based on the French Riviera",
    available247: "Available 24/7 — flight tracking included",
    insured: "Professional chauffeurs, business insurance, VTC license",
    fixedNoSurprise: "Flat rates, no hidden fees",
    cta: "Book or request a quote",
  },
  ar: {
    eyebrow: "الأسعار والثقة",
    heading: "أسعار ثابتة شفافة، خدمة راقية",
    intro:
      "على عكس معظم الوكالات، نعرض أسعارنا. أسعار ثابتة محددة مسبقاً — دون مفاجآت عند الوصول.",
    forfaitsHeading: "أسعار السائق الثابتة (ابتداءً من)",
    fromWord: "من",
    perKmNote: (perKm) =>
      `بعد الأسعار الثابتة: من ${perKm} يورو/كم. عرض سعر فوري لأي مسار آخر.`,
    trustHeading: "لماذا تثق بنا",
    ratingItem: (value, count) =>
      `تقييم ${value}/5 — ${count} مراجعة على Google`,
    sirenItem: (siren) => `شركة مسجّلة — SIREN ${siren}`,
    since2022: "نعمل منذ 2022، مقرّنا في الريفييرا الفرنسية",
    available247: "متاح على مدار الساعة — مع متابعة الرحلات الجوية",
    insured: "سائقون محترفون، تأمين تجاري، رخصة VTC",
    fixedNoSurprise: "أسعار ثابتة، دون رسوم خفية",
    cta: "احجز أو اطلب عرض سعر",
  },
};

type Props = {
  locale?: Locale;
  /** Cible du CTA (ancre formulaire embarqué / réservation). */
  ctaHref: string;
};

export function ChauffeurForfaitsTrust({
  locale = "fr",
  ctaHref,
}: Props): JSX.Element {
  const t = tr(UI, locale);
  const base = VEHICLES.CLASS_E; // classe la plus accessible → « à partir de »

  const fmtEuro = (n: number): string => {
    const sep = locale === "ar" ? "٬" : locale === "en" ? "," : " ";
    const digits = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    if (locale === "en") return `€${digits}`;
    if (locale === "ar") return `${digits} يورو`;
    return `${digits} €`;
  };
  const perKm = base.pricePerKm
    .toFixed(2)
    .replace(".", locale === "fr" ? "," : ".");

  const trustItems = [
    AGGREGATE_RATING
      ? {
          icon: "ph:star-fill",
          text: t.ratingItem(
            String(AGGREGATE_RATING.ratingValue).replace(
              ".",
              locale === "fr" ? "," : ".",
            ),
            AGGREGATE_RATING.reviewCount,
          ),
        }
      : null,
    {
      icon: "ph:identification-badge",
      text: t.sirenItem(
        COMPANY.legal.siren.replace(/\B(?=(\d{3})+(?!\d))/g, " "),
      ),
    },
    { icon: "ph:shield-check", text: t.since2022 },
    { icon: "ph:clock", text: t.available247 },
    { icon: "ph:seal-check", text: t.insured },
    { icon: "ph:tag", text: t.fixedNoSurprise },
  ].filter(Boolean) as { icon: string; text: string }[];

  return (
    <section className="bg-black py-14 text-white" lang={locale}>
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gold-300">
          {t.eyebrow}
        </p>
        <h2 className="mb-3 font-space-age text-xl tracking-[0.2em]">
          {t.heading}
        </h2>
        <p className="mb-7 max-w-3xl text-sm text-white/75">{t.intro}</p>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Forfaits réels */}
          <div className="border-white/12 rounded-2xl border bg-white/[0.04] p-5 md:p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-gold-200">
              {t.forfaitsHeading}
            </h3>
            <ul className="grid gap-2.5">
              {HEADLINE_ROUTES.map((route) => {
                const price = base.fixedTransfers[route.key];
                if (price === undefined) return null;
                return (
                  <li
                    key={route.key}
                    className="border-white/8 flex items-center justify-between gap-4 border-b pb-2.5 text-sm"
                  >
                    <span className="text-white/85">
                      {tr(route.label, locale)}
                    </span>
                    <span className="shrink-0 font-semibold text-white">
                      {t.fromWord} {fmtEuro(price)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-xs text-white/55">{t.perKmNote(perKm)}</p>
          </div>

          {/* Confiance / E-E-A-T réels */}
          <div className="border-white/12 rounded-2xl border bg-white/[0.04] p-5 md:p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-gold-200">
              {t.trustHeading}
            </h3>
            <ul className="grid gap-3">
              {trustItems.map((item) => (
                <li key={item.text} className="flex items-start gap-3 text-sm">
                  <Icon
                    icon={item.icon}
                    className="mt-0.5 h-4 w-4 shrink-0 text-gold-300"
                  />
                  <span className="text-white/85">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-7">
          <Link
            href={ctaHref}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white"
          >
            <Icon icon="mdi:calendar-check" className="h-4 w-4" />
            {t.cta}
          </Link>
        </div>
      </div>
    </section>
  );
}

export default ChauffeurForfaitsTrust;
